import {
  notFoundError,
  validationError,
  type AppError,
} from '@obraplan/core/application/errors/app-error';
import { ERROR_CODE } from '@obraplan/core/application/errors/error-codes';
import type { NonConformityRef } from '@obraplan/core/application/contracts/planning';
import type { Session } from '@obraplan/core/application/session';
import { ATTACHMENT_ERROR } from '@obraplan/core/domain/attachments/errors';
import { PLANNING_ERROR, PLANNING_ERROR_MESSAGE } from '@obraplan/core/domain/planning/errors';
import { err, ok, type Result } from '@obraplan/core/domain/shared/result';
import { PENDING_TEAM_MEASUREMENT_ID } from '@/features/planning/fila-da-equipe-e-do-rdo';
import { ncDaFoto, semNcGravada } from '@/features/planning/subir-fotos-das-ncs';
import type { Container } from './container';
import {
  PENDING_INSPECTION_ID,
  type ConferenceOperation,
  type OperationOutcome,
} from './outbox-types';

/** Os casos de uso que as operações da Conferência usam. */
export type ConferenceUseCases = Pick<
  Container,
  | 'openInspection'
  | 'adjustInspectionAllocation'
  | 'recordPieceExecutions'
  | 'recordDayMeasurement'
  | 'attachNonConformityPhoto'
  | 'confirmInspection'
  | 'registerInspectionAbsence'
  | 'reopenInspection'
  | 'attachInspectionFile'
  // Etapa 3: a medição por equipe e o RDO.
  | 'recordTeamMeasurement'
  | 'attachTeamMeasurementPhoto'
  | 'setDailyReportWeather'
  | 'setDailyReportTasks'
  | 'setDailyReportNotes'
  | 'setDailyReportCrew'
  | 'setDailyReportEquipment'
  | 'getOrOpenDailyReport'
  | 'attachDailyReportPhoto'
  // Etapa 4: a execução por cômodo.
  | 'setRoomNotApplicable'
  | 'recordRoomProgress'
  | 'getFrontPieces'
  // Lançar o que ninguém planejou (RN-617).
  | 'recordReportedWork'
  | 'recordReportedTeamWork'
  // Tirar a frente do dia (RN-620).
  | 'removeInspectionAllocation'
  // As ações que pediam sinal (RN-621 a RN-625).
  | 'removeInspectionAttachment'
  | 'resolveNonConformity'
  | 'closeDailyReport'
  | 'reviseDailyReport'
  // A foto da equipe medida sem sinal acha a medição pela frente (achado 4 da varredura).
  | 'getDayTeamMeasurements'
>;

/**
 * O servidor não respondeu — rede caída, ou quem respondeu não foi a API (portal do Wi-Fi,
 * proxy). É o que guarda a operação na fila; a recusa do servidor não guarda (RN-603).
 */
export function semResposta(error: AppError): boolean {
  return error.code === ERROR_CODE.NO_CONNECTION || error.code === ERROR_CODE.UNREADABLE_RESPONSE;
}

const NADA: OperationOutcome = {
  measured: 0,
  verified: 0,
  nonConformities: 0,
  adjusted: 0,
  confirmed: false,
  pieces: 0,
  photos: { sent: 0, failures: [] },
};

function bytesDe(file: File): Promise<Uint8Array> {
  return file.arrayBuffer().then((buffer) => new Uint8Array(buffer));
}

/** O id da conferência: o que a tela tinha, ou o que o servidor devolve ao abri-la (RN-100). */
async function conferenciaDe(
  uc: ConferenceUseCases,
  operation: ConferenceOperation & { readonly inspectionId?: string },
  session: Session,
): Promise<Result<string, AppError>> {
  if (operation.inspectionId !== undefined && operation.inspectionId !== PENDING_INSPECTION_ID) {
    return ok(operation.inspectionId);
  }
  const aberta = await uc.openInspection.execute(
    { projectId: operation.projectId, resourceId: operation.resourceId, date: operation.date },
    session,
  );
  return aberta.ok ? ok(aberta.value.id as string) : aberta;
}

/**
 * A lista do dia com o que entra e o que sai: as que saem saem, as que entram entram, e as outras
 * ficam como estão — inclusive as que outro aparelho marcou enquanto este estava sem sinal.
 */
export function aplicarMudanca(
  atuais: readonly string[],
  mudanca: { readonly add: readonly string[]; readonly remove: readonly string[] },
): string[] {
  const sai = new Set(mudanca.remove);
  return [...new Set([...atuais.filter((nome) => !sai.has(nome)), ...mudanca.add])];
}

/**
 * A lista de peças do dia a gravar na caixa "medir" (Fase 43, RN-615).
 *
 * A lista do servidor substitui as marcas daquele dia na frente × serviço (D22). Por isso vai a
 * que o servidor tem agora, com a diferença entre o que a pessoa viu ao abrir (`vistas`) e o que
 * ela escolheu. O registro guardado antes desta regra não tem `vistas`: vai como estava.
 */
async function listaDoDia(
  uc: ConferenceUseCases,
  session: Session,
  frente: { projectId: string; activityId: string; serviceId: string; date: string },
  escolhidas: readonly string[],
  vistas: readonly string[] | undefined,
): Promise<Result<readonly string[], AppError>> {
  if (vistas === undefined) return ok(escolhidas);
  const lidas = await uc.getFrontPieces.execute(frente, session);
  if (!lidas.ok) return lidas;
  const atuais = lidas.value.pieces
    .filter((piece) => piece.executedOn === frente.date)
    .map((piece) => piece.name);
  return ok(
    aplicarMudanca(atuais, {
      add: escolhidas.filter((nome) => !vistas.includes(nome)),
      remove: vistas.filter((nome) => !escolhidas.includes(nome)),
    }),
  );
}

/**
 * Executa uma operação da Conferência, passo a passo (ADR-194).
 *
 * É a mesma sequência de antes da Fase 43 — a tela a fazia direto —, agora num lugar só, para a
 * tela e a fila rodarem a mesma coisa. Um passo recusado para a sequência ali, com a recusa do
 * servidor. A falta de resposta também para, e devolve o erro de conexão: quem chamou decide
 * guardar, e a próxima tentativa refaz os passos com os mesmos ids, sem gravar duas vezes.
 */
export async function runConferenceOperation(
  operation: ConferenceOperation,
  uc: ConferenceUseCases,
  session: Session,
): Promise<Result<OperationOutcome, AppError>> {
  switch (operation.kind) {
    case 'ausencia': {
      const registrada = await uc.registerInspectionAbsence.execute(
        {
          projectId: operation.projectId,
          date: operation.date,
          resourceId: operation.resourceId,
          reason: operation.reason,
        },
        session,
      );
      return registrada.ok ? ok(NADA) : registrada;
    }

    case 'reabrir': {
      const id = await conferenciaDe(uc, operation, session);
      if (!id.ok) return id;
      const reaberta = await uc.reopenInspection.execute({ inspectionId: id.value }, session);
      return reaberta.ok ? ok(NADA) : reaberta;
    }

    case 'fotos-da-conferencia': {
      if (operation.files.length === 0) {
        return err(validationError('INPUT.INVALID', 'Nenhum arquivo escolhido.'));
      }
      const id = await conferenciaDe(uc, operation, session);
      if (!id.ok) return id;
      // Um arquivo por vez: o caso de uso verifica cota e limite a cada um, e enviar em
      // paralelo faria o décimo primeiro passar pela contagem antes de o décimo gravar.
      for (const file of operation.files) {
        const anexada = await uc.attachInspectionFile.execute(
          {
            inspectionId: id.value,
            fileName: file.name,
            declaredMimeType: file.type,
            bytes: await bytesDe(file),
            // Vazio é ausência, e o caso de uso a aceita aqui (RN-214).
            ...(operation.caption.trim() === '' ? {} : { caption: operation.caption.trim() }),
          },
          session,
        );
        if (!anexada.ok) return anexada;
      }
      return ok({ ...NADA, photos: { sent: operation.files.length, failures: [] } });
    }

    case 'fotos-da-nc': {
      if (operation.files.length === 0) {
        return err(validationError('INPUT.INVALID', 'Nenhum arquivo escolhido.'));
      }
      for (const file of operation.files) {
        const anexada = await uc.attachNonConformityPhoto.execute(
          {
            nonConformityId: operation.nonConformityId,
            fileName: file.name,
            declaredMimeType: file.type,
            bytes: await bytesDe(file),
          },
          session,
        );
        if (!anexada.ok) return anexada;
      }
      return ok({ ...NADA, photos: { sent: operation.files.length, failures: [] } });
    }

    case 'medir':
      return medir(operation, uc, session);

    case 'medir-equipe':
      return medirEquipe(operation, uc, session);

    case 'lancar-trabalho':
    case 'lancar-trabalho-equipe':
      return lancar(operation, uc, session);

    case 'tirar-frente': {
      const tirada = await uc.removeInspectionAllocation.execute(
        { allocationId: operation.allocationId, expectedVersion: operation.expectedVersion },
        session,
      );
      return tirada.ok ? ok({ ...NADA, removedFront: tirada.value }) : tirada;
    }

    // O "não se aplica" antes, uma vez por cômodo da tipologia: o percentual de um cômodo fora da
    // conta seria recusado (RN-444). Depois, o percentual de todos os cômodos de uma vez.
    case 'comodos': {
      const { projectId, activityId, serviceId, date } = operation;
      for (const marca of operation.notApplicable) {
        const marcado = await uc.setRoomNotApplicable.execute(
          { projectId, serviceId, roomId: marca.roomId, notApplicable: marca.notApplicable },
          session,
        );
        if (!marcado.ok) return marcado;
      }
      if (operation.progress.length > 0) {
        const apontado = await uc.recordRoomProgress.execute(
          { projectId, activityId, serviceId, date, rooms: operation.progress },
          session,
        );
        if (!apontado.ok) return apontado;
      }
      return ok(NADA);
    }

    // A lista de cada dia substitui as marcas daquele dia (D22), uma por vez e na ordem.
    case 'pecas-da-frente': {
      const { projectId, activityId, serviceId } = operation;
      // A lista de cada dia como o servidor a tem agora: a mudança vai por cima dela, e não por
      // cima da que o aparelho via quando estava sem sinal.
      const lidas = await uc.getFrontPieces.execute(
        { projectId, activityId, serviceId, date: operation.date },
        session,
      );
      if (!lidas.ok) return lidas;
      const executadaEm = new Map(
        lidas.value.pieces.map((piece) => [piece.name, piece.executedOn] as const),
      );
      let marked = 0;
      let unmarked = 0;
      for (const mudanca of operation.changes) {
        const doDia = [...executadaEm]
          .filter(([, dia]) => dia === mudanca.date)
          .map(([nome]) => nome);
        const gravada = await uc.recordPieceExecutions.execute(
          {
            projectId,
            activityId,
            serviceId,
            date: mudanca.date,
            pieceNames: aplicarMudanca(doDia, mudanca),
          },
          session,
        );
        if (!gravada.ok) return gravada;
        for (const nome of mudanca.remove) executadaEm.set(nome, undefined);
        for (const nome of mudanca.add) executadaEm.set(nome, mudanca.date);
        marked += gravada.value.marked;
        unmarked += gravada.value.unmarked;
      }
      return ok({ ...NADA, pieceMarks: { marked, unmarked } });
    }

    case 'fotos-da-equipe': {
      if (operation.files.length === 0) {
        return err(validationError('INPUT.INVALID', 'Nenhum arquivo escolhido.'));
      }
      const medicao = await medicaoDaEquipe(uc, operation, session);
      if (!medicao.ok) return medicao;
      // Um arquivo por vez, pelo mesmo motivo da foto da conferência: a cota é contada a cada um.
      for (const file of operation.files) {
        const anexada = await uc.attachTeamMeasurementPhoto.execute(
          {
            teamMeasurementId: medicao.value,
            fileName: file.name,
            declaredMimeType: file.type,
            bytes: await bytesDe(file),
            ...(operation.caption.trim() === '' ? {} : { caption: operation.caption.trim() }),
          },
          session,
        );
        if (!anexada.ok) return anexada;
      }
      return ok({ ...NADA, photos: { sent: operation.files.length, failures: [] } });
    }

    // O RDO grava pelo dia (obra × data), e o primeiro gesto cria o documento: nenhum destes
    // precisa do id dele (RN-612).
    case 'rdo-clima': {
      const { projectId, date, shifts } = operation;
      const gravado = await uc.setDailyReportWeather.execute({ projectId, date, shifts }, session);
      return gravado.ok ? ok(NADA) : gravado;
    }
    case 'rdo-tarefas': {
      const { projectId, date, tasks } = operation;
      const gravado = await uc.setDailyReportTasks.execute({ projectId, date, tasks }, session);
      return gravado.ok ? ok(NADA) : gravado;
    }
    case 'rdo-ocorrencias': {
      const { projectId, date, notes } = operation;
      const gravado = await uc.setDailyReportNotes.execute({ projectId, date, notes }, session);
      return gravado.ok ? ok(NADA) : gravado;
    }
    case 'rdo-efetivo': {
      const { projectId, date, crew } = operation;
      const gravado = await uc.setDailyReportCrew.execute({ projectId, date, crew }, session);
      return gravado.ok ? ok(NADA) : gravado;
    }
    case 'rdo-equipamento': {
      const { projectId, date, equipment } = operation;
      const gravado = await uc.setDailyReportEquipment.execute(
        { projectId, date, equipment },
        session,
      );
      return gravado.ok ? ok(NADA) : gravado;
    }
    case 'rdo-fotos': {
      if (operation.files.length === 0) {
        return err(validationError('INPUT.INVALID', 'Nenhum arquivo escolhido.'));
      }
      // A foto precisa de um dono com id, e o dono é o rascunho: o primeiro passo o abre.
      let dono = operation.reportId;
      if (dono === undefined) {
        const aberto = await uc.getOrOpenDailyReport.execute(
          { projectId: operation.projectId, date: operation.date },
          session,
        );
        if (!aberto.ok) return aberto;
        dono = aberto.value.id;
      }
      for (const file of operation.files) {
        const anexada = await uc.attachDailyReportPhoto.execute(
          {
            reportId: dono,
            fileName: file.name,
            declaredMimeType: file.type,
            bytes: await bytesDe(file),
            caption: operation.caption,
          },
          session,
        );
        if (!anexada.ok) return anexada;
      }
      return ok({ ...NADA, photos: { sent: operation.files.length, failures: [] } });
    }

    // A foto que já subiu sai pelo id (RN-621). A que outra pessoa tirou antes também conta: o
    // que a pessoa pediu — a foto fora — aconteceu, e não há o que revisar.
    case 'remover-foto': {
      const removida = await uc.removeInspectionAttachment.execute(
        { attachmentId: operation.attachmentId },
        session,
      );
      if (removida.ok || removida.error.code === ATTACHMENT_ERROR.NOT_FOUND) return ok(NADA);
      return removida;
    }

    // Com a versão que a tela leu (RN-623): a NC que outra pessoa encerrou ou cancelou enquanto o
    // aparelho estava sem sinal é recusada, e fica no cartão para revisar.
    case 'resolver-nc': {
      const { nonConformityId, solution, expectedVersion } = operation;
      const resolvida = await uc.resolveNonConformity.execute(
        { nonConformityId, solution, expectedVersion },
        session,
      );
      return resolvida.ok ? ok(NADA) : resolvida;
    }

    // O número é do servidor, e só ele o dá (RN-209, RN-624): a emissão feita sem sinal recebe o
    // número quando sobe, e o mesmo passo de novo devolve o mesmo número (RN-606).
    case 'rdo-emitir': {
      const emitido = await uc.closeDailyReport.execute(
        { projectId: operation.projectId, date: operation.date },
        session,
      );
      if (!emitido.ok) return emitido;
      return ok({
        ...NADA,
        report: { number: emitido.value.number ?? 0, revision: emitido.value.revision },
      });
    }
    case 'rdo-revisar': {
      const revisado = await uc.reviseDailyReport.execute(
        { projectId: operation.projectId, date: operation.date },
        session,
      );
      return revisado.ok ? ok(NADA) : revisado;
    }
  }
}

/**
 * A medição em que a foto da equipe entra (RN-622). A que a tela tinha vale; a medida sem sinal
 * ainda não tinha id, e a operação dela subiu antes desta, na mesma chave da frente: o id vem da
 * leitura do dia, pela equipe e pela frente — como a NC acha a foto pelo item.
 *
 * A leitura acontece em toda tentativa de uma foto sem id, e por isso a numeração dos passos é a
 * mesma de uma tentativa para a outra (RN-606).
 */
async function medicaoDaEquipe(
  uc: ConferenceUseCases,
  operation: Extract<ConferenceOperation, { kind: 'fotos-da-equipe' }>,
  session: Session,
): Promise<Result<string, AppError>> {
  const { teamMeasurementId, teamId, activityId, serviceId } = operation;
  if (teamMeasurementId !== undefined && teamMeasurementId !== PENDING_TEAM_MEASUREMENT_ID) {
    return ok(teamMeasurementId);
  }
  const lida = await uc.getDayTeamMeasurements.execute(
    { projectId: operation.projectId, date: operation.date },
    session,
  );
  if (!lida.ok) return lida;
  const id = lida.value.teams
    .find((team) => team.teamId === teamId)
    ?.fronts.find((front) => front.activityId === activityId && front.serviceId === serviceId)
    ?.measurement?.id;
  // A medição foi tirada, ou nunca chegou: a foto não tem onde entrar, e a recusa fica no cartão.
  return id === undefined
    ? err(
        notFoundError(
          PLANNING_ERROR.TEAM_MEASUREMENT_NOT_FOUND,
          PLANNING_ERROR_MESSAGE[PLANNING_ERROR.TEAM_MEASUREMENT_NOT_FOUND]!,
        ),
      )
    : ok(id);
}

/**
 * As fotos escolhidas nos itens reprovados, cada uma na NC do seu item (RN-479).
 *
 * A foto recusada não segura o resto (ela é dita na mensagem); a que ficou sem resposta segura,
 * e a operação inteira volta para a fila.
 */
async function subirAsFotosDasNcs(
  uc: ConferenceUseCases,
  session: Session,
  pendentes: Extract<ConferenceOperation, { kind: 'medir' }>['photos'],
  refs: readonly NonConformityRef[],
): Promise<Result<{ sent: number; failures: readonly string[] }, AppError>> {
  let enviadas = 0;
  const falhas: string[] = [];
  for (const pendente of pendentes) {
    const ref = ncDaFoto(pendente, refs);
    if (ref === undefined) {
      falhas.push(...pendente.files.map(semNcGravada));
      continue;
    }
    for (const file of pendente.files) {
      const anexada = await uc.attachNonConformityPhoto.execute(
        {
          nonConformityId: ref.nonConformityId,
          fileName: file.name,
          declaredMimeType: file.type,
          bytes: await bytesDe(file),
        },
        session,
      );
      if (anexada.ok) enviadas += 1;
      else if (semResposta(anexada.error)) return anexada;
      else falhas.push(`${file.name}: ${anexada.error.message}`);
    }
  }
  return ok({ sent: enviadas, failures: falhas });
}

/**
 * Lançar o que ninguém planejou (RN-617), da pessoa ou da equipe, na ordem do lançamento com
 * sinal: o "não se aplica" e o percentual dos cômodos (RN-443, RN-444), as peças antes da
 * quantidade (RN-381), o trabalho com a checklist e, com os números das NCs, as fotos delas.
 */
async function lancar(
  operation: Extract<ConferenceOperation, { kind: 'lancar-trabalho' | 'lancar-trabalho-equipe' }>,
  uc: ConferenceUseCases,
  session: Session,
): Promise<Result<OperationOutcome, AppError>> {
  const { projectId, date } = operation;
  const { pieceNames, roomChanges, photos, ...trabalho } = operation.input;
  const { activityId, serviceId } = trabalho;

  if (roomChanges !== undefined && serviceId !== undefined) {
    for (const marca of roomChanges.notApplicable) {
      const feito = await uc.setRoomNotApplicable.execute(
        { projectId, serviceId, ...marca },
        session,
      );
      if (!feito.ok) return feito;
    }
    if (roomChanges.rooms.length > 0) {
      const apontado = await uc.recordRoomProgress.execute(
        { projectId, activityId, serviceId, date, rooms: roomChanges.rooms },
        session,
      );
      if (!apontado.ok) return apontado;
    }
  }
  if (pieceNames !== undefined && serviceId !== undefined) {
    const marcado = await uc.recordPieceExecutions.execute(
      { projectId, activityId, serviceId, date, pieceNames },
      session,
    );
    if (!marcado.ok) return marcado;
  }

  const lancado =
    operation.kind === 'lancar-trabalho'
      ? await uc.recordReportedWork.execute({ projectId, date, ...trabalho }, session)
      : await uc.recordReportedTeamWork.execute({ projectId, date, ...trabalho }, session);
  if (!lancado.ok) return lancado;

  const fotos = await subirAsFotosDasNcs(
    uc,
    session,
    photos ?? [],
    lancado.value.nonConformityRefs,
  );
  if (!fotos.ok) return fotos;

  return ok({
    ...NADA,
    nonConformities: lancado.value.nonConformities,
    pieces: pieceNames === undefined ? 0 : 1,
    photos: fotos.value,
  });
}

/**
 * A medição da equipe (RN-406): as peças antes da quantidade (RN-381, RN-414), a quantidade e a
 * checklist num envelope só, e as fotos das NCs. A marca das peças vai sem alocação — a
 * quantidade é da equipe, e não de uma pessoa.
 */
async function medirEquipe(
  operation: Extract<ConferenceOperation, { kind: 'medir-equipe' }>,
  uc: ConferenceUseCases,
  session: Session,
): Promise<Result<OperationOutcome, AppError>> {
  const { projectId, date, teamId, activityId, serviceId } = operation;
  if (operation.pieceNames !== undefined) {
    const lista = await listaDoDia(
      uc,
      session,
      { projectId, activityId, serviceId, date },
      operation.pieceNames,
      operation.previousPieceNames,
    );
    if (!lista.ok) return lista;
    const marcado = await uc.recordPieceExecutions.execute(
      { projectId, activityId, serviceId, date, pieceNames: lista.value },
      session,
    );
    if (!marcado.ok) return marcado;
  }
  const medido = await uc.recordTeamMeasurement.execute(
    {
      projectId,
      date,
      teamId,
      activityId,
      serviceId,
      quantity: operation.quantity,
      verifications: operation.verifications,
    },
    session,
  );
  if (!medido.ok) return medido;

  const fotos = await subirAsFotosDasNcs(
    uc,
    session,
    operation.photos,
    medido.value.nonConformityRefs,
  );
  if (!fotos.ok) return fotos;

  return ok({
    ...NADA,
    measured: operation.quantity === null ? 0 : 1,
    nonConformities: medido.value.nonConformities,
    pieces: operation.pieceNames === undefined ? 0 : 1,
    photos: fotos.value,
    teamMeasurementId: medido.value.measurementId,
  });
}

/**
 * O dia da pessoa numa sequência: as horas ajustadas, todas as frentes numa chamada só (RN-104),
 * as peças marcadas, a medição e a checklist, as fotos das NCs e, por fim, o veredito.
 *
 * Confirmar só acontece **sem ajuste de horas**: ajustar já encerra a conferência como ADJUSTED
 * (RN-104), e pedir CONFIRMED depois seria pedir para encerrar o que acabou de encerrar.
 */
async function medir(
  operation: Extract<ConferenceOperation, { kind: 'medir' }>,
  uc: ConferenceUseCases,
  session: Session,
): Promise<Result<OperationOutcome, AppError>> {
  const id = await conferenciaDe(uc, operation, session);
  if (!id.ok) return id;

  const [primeiro, ...outros] = operation.adjustments;
  if (primeiro !== undefined) {
    const ajustado = await uc.adjustInspectionAllocation.execute(
      {
        allocationId: primeiro.allocationId,
        expectedVersion: primeiro.expectedVersion,
        quarterHours: primeiro.quarterHours,
        ...(outros.length === 0 ? {} : { otherAllocations: outros }),
        ...(operation.justification === undefined
          ? {}
          : { justification: operation.justification }),
      },
      session,
    );
    if (!ajustado.ok) return ajustado;
  }

  // As peças marcadas vão antes da quantidade (RN-381): uma peça já executada em outro dia é
  // recusada pelo nome, e a quantidade que dependia dela não chega a ser gravada.
  for (const marca of operation.pieces) {
    const frente = {
      projectId: operation.projectId,
      activityId: marca.activityId,
      serviceId: marca.serviceId,
      date: operation.date,
    };
    const lista = await listaDoDia(uc, session, frente, marca.pieceNames, marca.previousPieceNames);
    if (!lista.ok) return lista;
    const marcado = await uc.recordPieceExecutions.execute(
      { ...frente, pieceNames: lista.value, allocationId: marca.allocationId },
      session,
    );
    if (!marcado.ok) return marcado;
  }

  const semMedicao = operation.measurements.length === 0 && operation.verifications.length === 0;
  const medido = semMedicao
    ? ok({ measured: 0, verified: 0, nonConformities: 0, nonConformityRefs: [] })
    : await uc.recordDayMeasurement.execute(
        {
          inspectionId: id.value,
          measurements: operation.measurements,
          verifications: operation.verifications,
        },
        session,
      );
  if (!medido.ok) return medido;

  // As fotos do modal: a NC já tem número, e é por ele que cada uma acha o dono (RN-479).
  const fotos = await subirAsFotosDasNcs(
    uc,
    session,
    operation.photos,
    medido.value.nonConformityRefs,
  );
  if (!fotos.ok) return fotos;

  const ajustadas = operation.adjustments.length;
  const confirmar = operation.confirm && ajustadas === 0;
  if (confirmar) {
    const confirmado = await uc.confirmInspection.execute(
      { projectId: operation.projectId, resourceId: operation.resourceId, date: operation.date },
      session,
    );
    if (!confirmado.ok) return confirmado;
  }

  return ok({
    measured: medido.value.measured,
    verified: medido.value.verified,
    nonConformities: medido.value.nonConformities,
    adjusted: ajustadas,
    confirmed: confirmar,
    pieces: operation.pieces.length,
    photos: fotos.value,
  });
}
